import {getBrowserClient} from "@/lib/supabase";
import {fieldCheckErrorMessage, toKatakana, normalizePostalCodeInput, isValidPostalCode, companyNameHasContent, COMPANY_NAME_MAX_LENGTH} from "@sakura-grp/lib";
import {normalizeName} from "@/lib/normalize-name";

// 郵便番号の正規化・検証は @sakura-grp/lib に集約（admin と共用）。
// 既存の `@/lib/api/business-cards` 参照を壊さないよう re-export する。
export {normalizePostalCodeInput, isValidPostalCode};

export interface CreateBusinessCardInput {
  nameKanji: string;
  /** 必須。ひらがな入力はカタカナへ正規化して送信する */
  nameFurigana: string;
  mobileNumber?: string;
  emailAddress?: string;
  companyName?: string;
  /** 任意。名刺の住所。新規顧客の address_full / 新規会社の address に伝播する */
  address?: string;
  /** 任意。NNN-NNNN へ正規化して送信（DB の既存慣行 97.4% がハイフン付き） */
  postalCode?: string;
}

export interface CreateBusinessCardResult {
  cardId: string;
  /** 必ず顧客が作成/リンクされる（RPC が不変条件として保証。失敗時はカードごとロールバック+エラー） */
  customerId: string;
  storeId: string;
}

export interface CompanySuggestion {
  id: string;
  name: string;
}

/** 空文字・空白のみは undefined に落とし、RPC 側の DEFAULT NULL に委ねる */
function trimmedOrUndefined(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * 会社名が入力されている場合の検証（G3）。未入力（会社リンクなし）は許可。
 * 記号・空白のみ（「！！！」等）と 200 字超を保存前に弾く（無言で company_id を
 * 据え置かない）。法人格のみ（正規化して空）はサーバ RPC が明示エラーにする。
 */
function assertValidCompanyName(name: string | undefined): void {
  const trimmed = name?.trim();
  if (!trimmed) return;
  if (!companyNameHasContent(trimmed)) {
    throw new Error("会社名には文字または数字を含めてください");
  }
  if (trimmed.length > COMPANY_NAME_MAX_LENGTH) {
    throw new Error(`会社名は${COMPANY_NAME_MAX_LENGTH}文字以内で入力してください`);
  }
}

/**
 * 携帯電話入力の正規化: 全角数字・全角記号を半角化し、空白・括弧を除去する。
 * 全角数字のまま保存すると DB の normalize_phone() が数字を全削除して NULL を返し、
 * 顧客の自動作成・重複判定が一切効かなくなるため、送信前に必ず通す。
 */
export function normalizeMobileInput(value: string): string {
  return value
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/＋/g, "+")
    .replace(/[ー－‐−–—]/g, "-")
    .replace(/[\s　()（）]/g, "");
}

/** 正規化済みの携帯番号が有効か（数字10〜15桁、ハイフン任意、先頭 + 可） */
export function isValidMobileNumber(normalized: string): boolean {
  if (!/^\+?[\d-]+$/.test(normalized)) return false;
  const digitCount = normalized.replace(/\D/g, "").length;
  return digitCount >= 10 && digitCount <= 15;
}

/** メールアドレスの形式チェック（EmailForm と同一パターン） */
export function isValidEmailAddress(value: string): boolean {
  return /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(value);
}

/** フリガナがカタカナ（+長音・中点・空白）のみか。判定前に toKatakana 済みであること */
export function isValidKana(katakana: string): boolean {
  return /^[ァ-ヶー・\s　]+$/.test(katakana);
}

/**
 * ホステス本人として名刺（画像なし）を手動作成する。
 * store_id・hostess_id はサーバー側（RPC）で auth.uid() から導出される。
 * 入力契約 v3 (2026-06-11): 氏名・フリガナ必須 / 携帯・メール・会社は任意。
 * 連絡先なしでも顧客は必ず作成される（形式チェックは「入れるなら正しく」で RPC と同一契約）。
 */
export async function createBusinessCard(input: CreateBusinessCardInput): Promise<CreateBusinessCardResult> {
  const supabase = getBrowserClient();
  assertValidCompanyName(input.companyName);
  const normalizedMobile = input.mobileNumber ? normalizeMobileInput(input.mobileNumber) : undefined;
  // 氏名・フリガナは normalizeName（全角スペース→半角 + trim）を通す。
  // 顧客重複判定は name / name_kana の完全一致で、既存データの空白規約は半角のため、
  // iOS かなキーボード既定の全角スペースをそのまま流すと重複顧客が作られる
  const {data, error} = await supabase.rpc("create_business_card_as_hostess", {
    p_name_kanji: normalizeName(input.nameKanji),
    p_name_furigana: toKatakana(normalizeName(input.nameFurigana)),
    p_mobile_number: trimmedOrUndefined(normalizedMobile),
    p_email_address: trimmedOrUndefined(input.emailAddress),
    p_company_name: trimmedOrUndefined(input.companyName),
    p_address: trimmedOrUndefined(input.address),
    p_postal_code: trimmedOrUndefined(input.postalCode ? normalizePostalCodeInput(input.postalCode) : undefined),
  });

  if (error) {
    throw new Error(`名刺の登録に失敗しました: ${error.message}`);
  }

  const result = data as {card_id: string; customer_id: string; store_id: string};
  return {
    cardId: result.card_id,
    customerId: result.customer_id,
    storeId: result.store_id,
  };
}

export interface UpdateBusinessCardInput {
  nameKanji: string;
  /** 必須。ひらがな入力はカタカナへ正規化して送信する */
  nameFurigana: string;
  mobileNumber?: string;
  emailAddress?: string;
  companyName?: string;
  position?: string;
  companyPhone?: string;
  address?: string;
  postalCode?: string;
  handwrittenNote?: string;
}

export interface BusinessCardFieldChecks {
  nameVerdict?: "confirmed" | "needs_review" | "corrected" | "wrong" | "withdrawn" | null;
  addressVerdict?: "confirmed" | "needs_review" | "corrected" | "wrong" | "withdrawn" | null;
  nameExpectedFingerprint: string;
  addressExpectedFingerprint: string;
  note?: string;
}

export async function checkBusinessCardField(cardId: string, field: "name" | "address", verdict: "confirmed" | "needs_review", expectedFingerprint: string, note?: string): Promise<void> {
  const {error} = await getBrowserClient().rpc("check_business_card_field", {
    p_card_id: cardId,
    p_field: field,
    p_verdict: verdict,
    p_expected_fingerprint: expectedFingerprint,
    ...(note?.trim() ? {p_check_note: note.trim()} : {}),
  });
  if (error) throw Object.assign(new Error(fieldCheckErrorMessage(error.code)), {code: error.code});
}

/**
 * ホステス本人として、自分がもらった名刺を編集する。
 * 認可（自分の名刺か）・会社名の company_id 再解決はサーバ側（RPC）が担う。
 * 正規化は create と同一パイプライン（名前の全角スペース→半角、フリガナのカタカナ化、
 * 携帯の半角化、郵便番号の NNN-NNNN 整形、空文字は undefined → RPC の DEFAULT NULL）。
 */
export async function updateBusinessCard(cardId: string, input: UpdateBusinessCardInput, fieldChecks?: BusinessCardFieldChecks): Promise<CreateBusinessCardResult> {
  const supabase = getBrowserClient();
  assertValidCompanyName(input.companyName);
  const normalizedMobile = input.mobileNumber ? normalizeMobileInput(input.mobileNumber) : undefined;
  const {data, error} = await supabase.rpc("update_business_card_as_hostess", {
    p_card_id: cardId,
    p_name_kanji: normalizeName(input.nameKanji),
    p_name_furigana: toKatakana(normalizeName(input.nameFurigana)),
    p_mobile_number: trimmedOrUndefined(normalizedMobile),
    p_email_address: trimmedOrUndefined(input.emailAddress),
    p_company_name: trimmedOrUndefined(input.companyName),
    p_position: trimmedOrUndefined(input.position),
    p_company_phone: trimmedOrUndefined(input.companyPhone),
    p_address: trimmedOrUndefined(input.address),
    p_postal_code: trimmedOrUndefined(input.postalCode ? normalizePostalCodeInput(input.postalCode) : undefined),
    p_handwritten_note: trimmedOrUndefined(input.handwrittenNote),
    ...(fieldChecks?.nameVerdict || fieldChecks?.addressVerdict
      ? {
          p_name_verdict: fieldChecks.nameVerdict ?? undefined,
          p_address_verdict: fieldChecks.addressVerdict ?? undefined,
          p_name_expected_fingerprint: fieldChecks.nameExpectedFingerprint,
          p_address_expected_fingerprint: fieldChecks.addressExpectedFingerprint,
          p_check_note: fieldChecks.note?.trim() || undefined,
        }
      : {}),
  });

  if (error) {
    throw Object.assign(new Error(error.code?.startsWith("BC") ? fieldCheckErrorMessage(error.code) : `名刺の更新に失敗しました: ${error.message}`), {code: error.code});
  }

  const result = data as {card_id: string; customer_id: string; store_id: string};
  return {
    cardId: result.card_id,
    customerId: result.customer_id,
    storeId: result.store_id,
  };
}

/**
 * 会社名のサジェスト検索（既存から選択 or 新規登録）。
 * RPC 側で法人格（株式会社等）を除去した正規化名に部分一致するため、
 * 「株式会社」の有無に関わらずヒットする。2文字未満は RPC が空を返す。
 */
export async function searchCompanies(query: string): Promise<CompanySuggestion[]> {
  const supabase = getBrowserClient();
  const {data, error} = await supabase.rpc("search_companies_as_hostess", {
    p_query: query,
    p_limit: 8,
  });

  if (error) {
    throw new Error(`会社の検索に失敗しました: ${error.message}`);
  }

  return (data ?? []) as CompanySuggestion[];
}

export interface CompanyUsage {
  companyId: string | null;
  companyName: string | null;
  cards: number;
  customers: number;
}

/**
 * 自分の名刺に紐づく会社の利用件数（名刺枚数・顧客数）。全体リネーム確認用。
 * company_id が無い名刺は cards/customers = 0 を返す。
 */
export async function getCompanyUsage(cardId: string): Promise<CompanyUsage> {
  const supabase = getBrowserClient();
  const {data, error} = await supabase.rpc("get_company_usage_as_hostess", {
    p_card_id: cardId,
  });

  if (error) {
    throw new Error(`会社情報の取得に失敗しました: ${error.message}`);
  }

  const r = data as {
    company_id: string | null;
    company_name: string | null;
    cards: number;
    customers: number;
  };
  return {
    companyId: r.company_id,
    companyName: r.company_name,
    cards: r.cards,
    customers: r.customers,
  };
}

export interface RenameCompanyResult {
  oldName: string;
  newName: string;
  cards: number;
  customers: number;
}

/**
 * 自分の名刺に紐づく会社の登録名を全体リネーム（company_id 不変＝全体反映）。
 * 別会社と同名になる場合はサーバ側で弾かれる（付け替えを使う）。エラー文言は
 * RPC のユーザー向けメッセージ（付け替え誘導等）をそのまま表示する。
 */
export async function renameCompany(cardId: string, newName: string): Promise<RenameCompanyResult> {
  const supabase = getBrowserClient();
  const {data, error} = await supabase.rpc("rename_company_as_hostess", {
    p_card_id: cardId,
    p_new_name: newName,
  });

  if (error) {
    throw new Error(error.message);
  }

  const r = data as {old_name: string; new_name: string; cards: number; customers: number};
  return {oldName: r.old_name, newName: r.new_name, cards: r.cards, customers: r.customers};
}
